/**
 * The FIRE to IRIS converter, in the browser.
 *
 * The file is read, checked and turned into IRIS XML here, in the tab, with the same engine
 * modules Tab Service uses to file for real: `parsePub1220` reads the fixed-width records
 * with the layout arrays that write them, and the per-form generators produce the XML. The
 * bytes never leave the machine, and nothing about this file is stored anywhere.
 *
 * What is deliberately not here: the IRS schema check (it needs a native XML library) and
 * the transmission itself. The XML this produces is the payload; sending it needs a TCC and
 * an IRIS connection.
 */
import {
  PAYEE_TARGETS_BY_FORM,
  parsePub1220,
  splitPub1220Lines,
  type Pub1220ParseResult,
} from '@engine/intake/pub1220';
import { B_RECORD_COMMON, PUB1220_RECORD_LENGTH, T_RECORD, type Pub1220Field } from '@engine/intake/pub1220Layouts';
import { AMOUNT_COLUMNS_BY_FORM, TYPE_OF_RETURN_TO_FORM } from '@engine/intake/pub1220Codes';
import { dispatchXml } from '@engine/build/dispatch';
import { chunkRecords } from '@engine/build/chunk';
import { generateRandomRecordId } from '@engine/constants/genRecordId';
import { checkRecords } from '@engine/validate/checkRecords';
import { irsCountryCode } from '@engine/validate/countryCodes.generated';
import { rewriteForeignAddresses } from '@engine/sanitize/foreignAddressRewrite';

export type Severity = 'blocking' | 'attention' | 'note';

export interface Finding {
  severity: Severity;
  code: string;
  title: string;
  detail: string;
  line?: number;
  payee?: string;
  /** What the person should do about it, in their own terms. */
  fix?: string;
}

export interface NameSplit {
  recordRef: string;
  line: number;
  /** The payee name exactly as the flat file writes it, surname first. */
  original: string;
  first: string;
  last: string;
  business: string;
  /** True when the split is worth a human glance before filing. */
  check: boolean;
  why?: string;
}

export interface FormBreakdown {
  formType: string;
  rawTypeOfReturn: string;
  supported: boolean;
  payees: number;
  transmissions: number;
}

export interface Transmitter {
  tcc: string;
  tin: string;
  name: string;
  /** The second name line, which IRIS writes as the transmitter's last name. */
  nameContinuation: string;
  companyName: string;
  companyNameContinuation: string;
  /**
   * The transmitter's own mailing address. The IRS schema requires it in two places - the
   * transmitter's CompanyGrp and the IssuerDetail - so a file converted without it is
   * refused by the schema check whatever the payee records say.
   */
  address: string;
  city: string;
  state: string;
  zip: string;
  contact: string;
  email: string;
  phone: string;
  testFile: boolean;
  priorYear: boolean;
  /** Foreign Entity Indicator (position 29): the address above is then free format. */
  foreign: boolean;
}

export interface ConversionResult {
  fileName: string;
  bytes: number;
  lines: number;
  taxYear: number | null;
  transmitter: Transmitter;
  forms: FormBreakdown[];
  issuers: { name: string; tin: string; formType: string | null; payees: number }[];
  records: CanonicalRecord[];
  payees: number;
  names: NameSplit[];
  amounts: { code: string; box: string; formType: string; payees: number }[];
  findings: Finding[];
  /** Every foreign address read from a free-format block, with how sure the reading is. */
  foreignAddresses: ForeignAddressEntry[];
  /** The first payee record as written, so the page can lay it out at its positions. */
  sampleRecordLine: string | null;
  /** True when nothing blocking was found: the XML can be produced. */
  convertible: boolean;
}

export type CanonicalRecord = Record<string, unknown>;

/**
 * One foreign address the engine read from a free-format city/state/ZIP block. Declared here,
 * not imported from the engine, so the published types stand on their own in the portal.
 */
export interface ForeignAddressEntry {
  line: number;
  /** Whose address: the payee, the issuer (A record), or the 1099-LTC insured. */
  role: 'payee' | 'issuer' | 'insured';
  /** The payee's account number (or line-N); the issuer's TIN for an issuer. */
  ref: string;
  formType: string | null;
  reading: {
    raw: string;
    city: string;
    province: string;
    provinceName: string;
    postalCode: string;
    countryCode: string | null;
    countryName: string;
    confidence: 'high' | 'medium' | 'low';
    reasons: string[];
  };
}

/** What the per-form generators read off the engine context. Nothing else is touched. */
interface EngineContextLike {
  irs: { name: string; tcc: string; softwareId: string; maxIntakeTaxYear: number };
  transmitter: Record<string, string>;
  lookups: { cfsfEnabled: () => Promise<boolean> };
  logger?: unknown;
}

/**
 * The largest file this tool will read. IRIS itself caps a transmission at 100 MB, so a
 * bigger 1220 file has to be split before it can be filed anyway, and a browser tab should
 * not be asked to hold more than that at once. A 39 MB file of 55,000 records reads in
 * under a second, so the limit is generous rather than tight.
 */
/** The year to check against when the T record does not name one. */
export const CURRENT_TAX_YEAR = 2026;

export const MAX_FILE_BYTES = 100 * 1024 * 1024;

/** The IRS record length, so the page can explain what a wrong length means. */
export const RECORD_LENGTH = PUB1220_RECORD_LENGTH;

/** Every Type of Return code this converter knows how to turn into IRIS XML. */
export const SUPPORTED_FORMS = new Set(Object.values(TYPE_OF_RETURN_TO_FORM) as string[]);

// ---------------------------------------------------------------------------
// Reading the transmitter (T) record
// ---------------------------------------------------------------------------

function offsets(layout: Pub1220Field[]): Map<string, { at: number; len: number }> {
  const map = new Map<string, { at: number; len: number }>();
  let at = 0;
  for (const field of layout) {
    if (field.key) map.set(field.key, { at, len: field.length });
    at += field.length;
  }
  return map;
}

const T_OFFSETS = offsets(T_RECORD);

function readField(line: string, key: string): string {
  const spec = T_OFFSETS.get(key);
  if (!spec) return '';
  return line.substring(spec.at, spec.at + spec.len).trim();
}

function readTransmitter(text: string): Transmitter {
  const line = splitLines(text).find((row) => row.trim().charAt(0)?.toUpperCase() === 'T') ?? '';
  const padded = line.padEnd(RECORD_LENGTH, ' ');
  return {
    tcc: readField(padded, 'TransmitterControlCode'),
    tin: readField(padded, 'TransmitterTIN'),
    name: readField(padded, 'TransmitterName') || readField(padded, 'CompanyName'),
    nameContinuation: readField(padded, 'TransmitterNameContinuation'),
    companyName: readField(padded, 'CompanyName') || readField(padded, 'TransmitterName'),
    companyNameContinuation: readField(padded, 'CompanyNameContinuation'),
    address: readField(padded, 'CompanyMailingAddress'),
    city: readField(padded, 'CompanyCity'),
    state: readField(padded, 'CompanyState'),
    zip: readField(padded, 'CompanyZIPCode'),
    contact: readField(padded, 'ContactName'),
    email: readField(padded, 'ContactEmailAddress'),
    phone: readField(padded, 'ContactTelephoneNumberAndExtension') || readField(padded, 'ContactTelephoneNumber'),
    testFile: readField(padded, 'TestFileIndicator').toUpperCase() === 'T',
    priorYear: readField(padded, 'PriorYearDataIndicator').toUpperCase() === 'P',
    foreign: readField(padded, 'ForeignEntityIndicator') === '1',
  };
}

// ---------------------------------------------------------------------------
// The payee record, laid out at its own positions
// ---------------------------------------------------------------------------

export interface RulerField {
  key: string;
  label: string;
  at: number;
  length: number;
  /** What IRIS calls the same fact, for the fields where the question comes up. */
  iris?: string;
  /** A run of fields the strip skips, drawn as an ellipsis. */
  gap?: boolean;
}

/** Plain words for the Pub 1220 field names, and where each fact goes in IRIS. */
const RULER_LABELS: Record<string, { label: string; iris?: string }> = {
  RecordType: { label: 'Record type' },
  PaymentYear: { label: 'Tax year', iris: 'TaxYr' },
  CorrectedReturnIndicator: { label: 'Corrected', iris: 'CorrectedInd' },
  NameControl: { label: 'Name control' },
  TypeOfTIN: { label: 'TIN type', iris: 'TINTypeCd' },
  PayeesTIN: { label: "Payee's TIN", iris: 'RecipientTIN' },
  IssuersAccountNumberForPayee: { label: 'Account number', iris: 'AccountNum' },
  PaymentAmount1: { label: 'Amount, code 1', iris: 'the box for code 1' },
  FirstPayeeNameLine: { label: 'Payee name, surname first', iris: 'first and last name' },
  PayeeMailingAddress: { label: 'Address', iris: 'AddressLine1Txt' },
  PayeeCity: { label: 'City', iris: 'CityNm' },
  PayeeState: { label: 'State', iris: 'StateAbbreviationCd' },
  PayeeZIPCode: { label: 'ZIP', iris: 'ZIPCd' },
};

/** The fields the strip shows, in file order, with everything between them elided. */
function buildRuler(): RulerField[] {
  const wanted = new Set(Object.keys(RULER_LABELS));
  const fields: RulerField[] = [];
  let at = 0;
  let skipped = false;
  for (const field of B_RECORD_COMMON) {
    if (field.key && wanted.has(field.key)) {
      if (skipped) {
        fields.push({ key: '', label: '', at, length: 0, gap: true });
        skipped = false;
      }
      const meta = RULER_LABELS[field.key];
      fields.push({ key: field.key, label: meta.label, iris: meta.iris, at, length: field.length });
    } else if (fields.length > 0) {
      skipped = true;
    }
    at += field.length;
  }
  return fields;
}

export const RULER_FIELDS: RulerField[] = buildRuler();

// ---------------------------------------------------------------------------
// Reading and checking a file
// ---------------------------------------------------------------------------

const NAME_SUFFIXES = new Set(['JR', 'SR', 'II', 'III', 'IV', 'V', 'MD', 'DDS', 'PHD', 'ESQ']);
const BUSINESS_WORDS = /\b(LLC|L\.L\.C|INC|CORP|CO|COMPANY|LTD|LP|LLP|PLLC|TRUST|FOUNDATION|ASSOCIATES|PARTNERS|GROUP|SERVICES|SOLUTIONS|ENTERPRISES|BANK|CHURCH|SCHOOL|UNIVERSITY)\b/i;

/**
 * Where a form keeps its payee. Most forms use RECIPIENT_*, but 1099-K, 1099-LTC, 1098-E,
 * 1098-T, 3921 and 3922 have their own columns (PAYEE_*, POLICYHOLDER_*, BORROWER_*,
 * STUDENT_*, EMPLOYEE_*), the ones parsePub1220 writes. Reading only RECIPIENT_* reported
 * every payee on those forms as having no name and no TIN, and blocked the file.
 */
function payeeKeys(formType: string) {
  return PAYEE_TARGETS_BY_FORM[formType] ?? PAYEE_TARGETS_BY_FORM['1099-NEC'];
}

const text = (value: unknown): string => String(value ?? '').trim();

/**
 * The form a payee record belongs to. parsePub1220 stamps it as FORM_TYPE from the record's
 * own A record. Reading only `formType`, which it never writes, fell back to the file's form
 * - null when a file holds several - or to the first form, so a file with NEC and INT
 * issuers wrote every payee as NEC.
 */
function formOf(record: CanonicalRecord, fallback?: string | null): string {
  return text(record.formType ?? record.FORM_TYPE ?? fallback ?? '');
}

/** The payee's name parts and TIN, read from the form's own columns. */
function payeeOf(record: CanonicalRecord, formType: string) {
  const keys = payeeKeys(formType);
  const tin = text(record[keys.tin]);
  if (keys.businessName === keys.firstName) {
    // One column holds a business name, or a person's given names with the surname in the
    // second column. A filled surname column is what says it is a person.
    const lead = text(record[keys.firstName]);
    const last = text(record[keys.lastName]);
    return last ? { first: lead, last, business: '', tin } : { first: '', last: '', business: lead, tin };
  }
  return {
    first: text(record[keys.firstName]),
    last: text(record[keys.lastName]),
    business: text(record[keys.businessName]),
    tin,
  };
}

/** A payee name the flat file wrote as one line, and how it will reach IRIS. */
function reviewName(record: CanonicalRecord, line: number, formType: string): NameSplit | null {
  const { first, last, business } = payeeOf(record, formType);
  // The middle name and suffix are part of what the file said: "SMITH CONSULTING LLC" filed
  // with an SSN splits into first CONSULTING, middle LLC, and only the whole line reads as a
  // business.
  const original = business ||
    [last, first, text(record.RECIPIENT_MIDDLE_NAME), text(record.RECIPIENT_SUFFIX)].filter(Boolean).join(' ');
  if (!original) return null;

  const split: NameSplit = {
    recordRef: String(record.recordRef ?? `line-${line}`),
    line,
    original,
    first,
    last,
    business,
    check: false,
  };

  if (business) {
    // A business name goes across whole; only a person's name is split.
    split.check = false;
    return split;
  }
  if (!first) {
    split.check = true;
    split.why = 'One word only, so IRIS gets a last name and no first name.';
    return split;
  }
  if (BUSINESS_WORDS.test(original)) {
    split.check = true;
    split.why = 'Reads like a business but is being filed as a person.';
    return split;
  }
  const tokens = first.split(/\s+/);
  if (tokens.some((token) => NAME_SUFFIXES.has(token.replace(/\./g, '').toUpperCase()))) {
    split.check = true;
    split.why = 'Carries a suffix (Jr, III). IRIS keeps the suffix separate from the first name.';
    return split;
  }
  if (tokens.length > 2) {
    split.check = true;
    split.why = 'More than two given names; check which part is the first name.';
  }
  return split;
}

function isBlankTin(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits === '' || /^0+$/.test(digits);
}

/**
 * The file's records, however the system that wrote it ended them: line breaks of any kind,
 * or none at all (750-character records back to back), the same way the parser reads them.
 */
function splitLines(text: string): string[] {
  return splitPub1220Lines(text).lines;
}

const ROLE_LABEL: Record<ForeignAddressEntry['role'], string> = {
  payee: 'payee',
  issuer: 'issuer (payer)',
  insured: '1099-LTC insured',
};

/**
 * One finding per kind of doubt about the foreign addresses: the ones that could not be read,
 * then the ones worth a glance. A payee address that could not be read blocks the XML, because
 * IRIS would get a wrong or missing country; everywhere else it is for a person to check.
 */
function foreignAddressFindings(addresses: ForeignAddressEntry[]): Finding[] {
  const findings: Finding[] = [];
  for (const role of ['payee', 'issuer', 'insured'] as const) {
    const ofRole = addresses.filter((entry) => entry.role === role);
    for (const level of ['low', 'medium'] as const) {
      const hits = ofRole.filter((entry) => entry.reading.confidence === level);
      if (hits.length === 0) continue;
      const first = hits[0];
      const refs = hits.slice(0, 5).map((entry) => entry.ref).join(', ');
      const label = ROLE_LABEL[role];
      findings.push({
        severity: level === 'low' && role === 'payee' ? 'blocking' : 'attention',
        code: level === 'low' ? 'FOREIGN_ADDRESS_UNREAD' : 'FOREIGN_ADDRESS_CHECK',
        title:
          level === 'low'
            ? `${hits.length} foreign ${label} address${hits.length === 1 ? '' : 'es'} could not be read safely`
            : `${hits.length} foreign ${label} address${hits.length === 1 ? '' : 'es'} worth a look`,
        detail:
          `${refs}${hits.length > 5 ? ' and more' : ''}. ` +
          `First: "${first.reading.raw}" (line ${first.line}): ${first.reading.reasons[0] ?? ''}`,
        line: first.line,
        fix:
          level === 'low'
            ? 'Pub 1220 wants "city, province or state, postal code, country name" in positions 448-498. Fix the address in the system that wrote the file, or confirm it from the foreign address report.'
            : 'Each reading is in the foreign address report, with the reason it is not certain. Check them against the client\'s records.',
      });
    }
    const clean = ofRole.filter((entry) => entry.reading.confidence === 'high').length;
    if (clean > 0) {
      findings.push({
        severity: 'note',
        code: 'FOREIGN_ADDRESS_READ',
        title: `${clean} foreign ${ROLE_LABEL[role]} address${clean === 1 ? '' : 'es'} read cleanly`,
        detail: 'Country, postal code and province were read from the free-format city, state and ZIP positions.',
      });
    }
  }
  return findings;
}

export function convertFireFile(fileName: string, text: string): ConversionResult {
  const parsed: Pub1220ParseResult = parsePub1220(text);
  const transmitter = readTransmitter(text);
  const sampleRecordLine =
    splitLines(text).find((row) => row.charAt(0).toUpperCase() === 'B' && row.trim().length > 100) ?? null;
  const lines = splitLines(text).filter((line) => line.trim() !== '').length;
  const findings: Finding[] = [];

  // 1. Structure, as the parser found it.
  for (const error of parsed.errors) {
    findings.push({
      severity: 'blocking',
      code: 'STRUCTURE',
      title: 'The file does not follow Publication 1220',
      detail: error.message,
      line: error.line,
      fix: `Every record is ${RECORD_LENGTH} characters and the file runs T, A, B..., C, F. Re-export it from the system that wrote it.`,
    });
  }
  for (const warning of parsed.warnings) {
    findings.push({
      severity: 'note',
      code: 'STRUCTURE_NOTE',
      title: 'Worth knowing',
      detail: warning.message,
      line: warning.line,
    });
  }

  // 2. The control records IRIS has no equivalent for, but a 1220 file must still have.
  const types = new Set(
    splitLines(text)
      .filter((line) => line.trim() !== '')
      .map((line) => line.charAt(0).toUpperCase()),
  );
  for (const [type, what] of [
    ['T', 'transmitter (T) record'],
    ['A', 'issuer (A) record'],
    ['B', 'payee (B) records'],
    ['C', 'end-of-issuer (C) record'],
    ['F', 'end-of-transmission (F) record'],
  ] as const) {
    if (!types.has(type)) {
      findings.push({
        severity: type === 'C' || type === 'F' ? 'attention' : 'blocking',
        code: `MISSING_${type}`,
        title: `No ${what}`,
        detail: `The file has no ${what}. FIRE would refuse it, and the totals cannot be checked.`,
        fix:
          type === 'C' || type === 'F'
            ? 'IRIS does not use these records, so the conversion still runs, but the file is not a valid 1220 file.'
            : 'Re-export the file from the system that produced it.',
      });
    }
  }

  // The transmitter's address goes into the IRIS CompanyGrp and IssuerDetail as a US address.
  // A foreign transmitter's free-format city/state/ZIP would be written there as if it were one.
  if (transmitter.foreign) {
    findings.push({
      severity: 'blocking',
      code: 'TRANSMITTER_FOREIGN',
      title: 'The transmitter is marked as a foreign entity',
      detail: `The T record's Foreign Entity Indicator is set, and its city, state and ZIP read "${[transmitter.city, transmitter.state, transmitter.zip].filter(Boolean).join(' ')}". This converter writes the transmitter address as a US address.`,
      fix: 'File through a transmitter with a US address, or have Tab Service set up a foreign transmitter address by hand.',
    });
  }

  if (!transmitter.tcc) {
    findings.push({
      severity: 'note',
      code: 'NO_TCC',
      title: 'No Transmitter Control Code in the T record',
      detail: 'The converted XML carries a placeholder where your TCC goes.',
      fix: 'A FIRE TCC does not work with IRIS. Apply for an IRIS TCC; the IRS says it can take up to 45 days.',
    });
  }

  // 3. Form types.
  const forms: FormBreakdown[] = [];
  for (const issuer of parsed.issuers) {
    const formType = issuer.formType ?? '';
    const existing = forms.find((entry) => entry.rawTypeOfReturn === issuer.rawTypeOfReturn);
    if (existing) {
      existing.payees += issuer.recordCount;
    } else {
      forms.push({
        formType: formType || `Type of Return "${issuer.rawTypeOfReturn}"`,
        rawTypeOfReturn: issuer.rawTypeOfReturn,
        supported: Boolean(formType) && SUPPORTED_FORMS.has(formType),
        payees: issuer.recordCount,
        transmissions: 0,
      });
    }
  }
  for (const form of forms) {
    form.transmissions = Math.max(1, chunkRecords(new Array(form.payees).fill({})).length);
    if (!form.supported) {
      findings.push({
        severity: 'blocking',
        code: 'UNSUPPORTED_FORM',
        title: `${form.formType} is not converted here`,
        detail: `${form.payees} payee record(s) carry Type of Return "${form.rawTypeOfReturn}", which this converter does not turn into IRIS XML.`,
        fix:
          form.rawTypeOfReturn === 'W'
            ? 'W-2G goes to the SSA, not to IRIS.'
            : 'This page converts the fifteen forms Publication 1220 shares with IRIS. 1042-S is specified by Publication 1187 and uses a Q record, so it is not a 1220 file at all.',
      });
    }
  }

  // 4. Every payee record.
  const names: NameSplit[] = [];
  const amountsSeen = new Map<string, { code: string; box: string; formType: string; payees: number }>();

  parsed.records.forEach((record, index) => {
    const line = Number(String(record.recordRef ?? '').replace(/^line-/, '')) || index + 1;
    const ref = String(record.recordRef ?? `line-${line}`);
    const formType = formOf(record, parsed.formType);
    const payee = payeeOf(record, formType);
    const payeeName = payee.business || [payee.last, payee.first].filter(Boolean).join(' ');
    const tin = payee.tin;
    if (isBlankTin(tin)) {
      findings.push({
        severity: 'attention',
        code: 'MISSING_TIN',
        title: 'No taxpayer ID',
        detail: `${payeeName || ref} has no TIN in the payee record.`,
        line,
        payee: payeeName || ref,
        fix: 'IRIS accepts a missing TIN but the IRS will notice. Fill it in, or file knowing a B-notice may follow.',
      });
    } else if (tin.replace(/\D/g, '').length !== 9) {
      findings.push({
        severity: 'blocking',
        code: 'BAD_TIN',
        title: 'Taxpayer ID is not nine digits',
        detail: `${payeeName || ref} has "${tin}".`,
        line,
        payee: payeeName || ref,
        fix: 'IRIS refuses anything but nine digits, with no dashes.',
      });
    }

    if (!payeeName) {
      findings.push({
        severity: 'blocking',
        code: 'MISSING_NAME',
        title: 'No payee name',
        detail: `The payee record on line ${line} has an empty name line.`,
        line,
        fix: 'Every IRIS record needs either a business name or a first and last name.',
      });
    }

    const split = reviewName(record, line, formType);
    if (split) names.push(split);

    // Amounts: what the file carried, and which IRIS box it lands in.
    const columns = AMOUNT_COLUMNS_BY_FORM[formType] ?? {};
    for (const [code, column] of Object.entries(columns)) {
      const value = record[column];
      if (value === undefined || value === null || value === '') continue;
      const key = `${formType}:${code}`;
      const seen = amountsSeen.get(key);
      if (seen) seen.payees += 1;
      else amountsSeen.set(key, { code, box: String(column), formType, payees: 1 });
      if (Number.isNaN(Number(value))) {
        findings.push({
          severity: 'blocking',
          code: 'BAD_AMOUNT',
          title: 'Amount is not a number',
          detail: `${payeeName || ref}: amount code ${code} reads "${String(value)}".`,
          line,
          payee: payeeName || ref,
          fix: 'Pub 1220 amounts are unsigned cents, zero filled. IRIS takes decimals.',
        });
      }
    }
  });

  // Corrected returns. A Pub 1220 correction is a B record with Corrected Return Indicator G
  // (amounts) or C (name or TIN), sent again as it should have been. An IRIS correction is
  // different: it must name the accepted original by its Unique Record ID (receipt ID,
  // submission ID and record ID), which IRIS issued and a FIRE file never carries. Written
  // as it was, these went out as new original returns, a second return for the same payee.
  const corrected = parsed.records
    .map((record, index) => ({
      indicator: String(record.CORRECTED_RETURN_INDICATOR ?? '').trim().toUpperCase(),
      // The account number, or line-N when the record has none.
      ref: String(record.recordRef ?? '').trim() || `payee ${index + 1}`,
    }))
    .filter(({ indicator }) => indicator === 'G' || indicator === 'C');
  if (corrected.length > 0) {
    const refs = corrected.slice(0, 5).map(({ ref }) => ref).join(', ');
    findings.push({
      severity: 'blocking',
      code: 'CORRECTED_RETURNS',
      title: `${corrected.length} corrected return${corrected.length === 1 ? '' : 's'} cannot be converted`,
      detail:
        `${refs}${corrected.length > 5 ? ' and more' : ''} ` +
        `${corrected.length === 1 ? 'carries' : 'carry'} Corrected Return Indicator G or C. ` +
        'An IRIS correction must name the accepted original by its Unique Record ID, which a FIRE file does not have, so converting them would file them as new originals.',
      fix: 'Take the corrected records out of this file and convert the rest. File each correction against its accepted original in Tab Service e-filing.',
    });
  }

  // 5. The same gate the filing service uses.
  //
  // Up to here the page has checked the FILE: its structure, its TINs, its names, its
  // amounts. This checks the RECORDS, per form, with checkRecords - the rules built from
  // core-api's own field definitions and the IRS country table. It is what catches a
  // 1099-R with no distribution code or a 3921 with no grant date: fields that live in the
  // form-specific tail of the payee record, which a converter that only reads names and
  // money would carry across as silence.
  //
  // Grouped by rule, because one missing column is usually missing on every row, and a
  // person needs to know that once with a count, not eight thousand times.
  const byForm = new Map<string, Record<string, unknown>[]>();
  for (const record of parsed.records) {
    const formType = formOf(record, parsed.formType);
    if (!SUPPORTED_FORMS.has(formType)) continue;
    const list = byForm.get(formType);
    if (list) list.push(record);
    else byForm.set(formType, [record]);
  }
  const checkYear = parsed.taxYear ?? CURRENT_TAX_YEAR;
  for (const [formType, formRecords] of byForm) {
    const grouped = new Map<string, { count: number; first: string; message: string }>();
    for (const issue of checkRecords(formType, checkYear, formRecords).issues) {
      // The year gate belongs to the app, which knows which year is being filed. Here the
      // year comes from the file itself and is only ever informational.
      if (issue.code === 'UNSUPPORTED_TAX_YEAR') continue;
      const key = `${issue.code}:${issue.field ?? ''}`;
      const seen = grouped.get(key);
      if (seen) seen.count += 1;
      else
        grouped.set(key, {
          count: 1,
          first: String(issue.recordRef ?? `row ${issue.index + 1}`),
          message: issue.message,
        });
    }
    for (const [key, group] of grouped) {
      const [code, field] = key.split(':');
      findings.push({
        severity: 'blocking',
        code,
        title: `${formType}: ${group.count} record${group.count === 1 ? '' : 's'} IRIS will not take`,
        detail: `${group.message} First seen at ${group.first}.`,
        payee: field || undefined,
        fix: field
          ? `Every one of these records needs ${field}. FIRE keeps it in the form-specific part of the payee record, positions 544 onward.`
          : undefined,
      });
    }
  }

  findings.push(...foreignAddressFindings(parsed.foreignAddresses));

  const needsCheck = names.filter((name) => name.check).length;
  if (needsCheck > 0) {
    findings.push({
      severity: 'attention',
      code: 'NAME_SPLIT',
      title: `${needsCheck} payee name${needsCheck === 1 ? '' : 's'} worth a look`,
      detail:
        'FIRE carries one name line; IRIS wants a first and a last name. Most split cleanly, these are the ones that did not.',
      fix: 'Check them below and correct any that read wrongly. Your corrections go into the download.',
    });
  }

  if (transmitter.testFile) {
    findings.push({
      severity: 'note',
      code: 'TEST_FILE',
      title: 'This is a test file',
      detail: 'The T record carries the test indicator, so the converted XML is marked as a test transmission too.',
    });
  }
  if (transmitter.priorYear) {
    findings.push({
      severity: 'note',
      code: 'PRIOR_YEAR',
      title: 'Prior year data',
      detail: 'The T record marks this as prior-year data. IRIS handles a prior year by its tax year, not by a flag.',
    });
  }
  if (types.has('K')) {
    findings.push({
      severity: 'note',
      code: 'STATE_TOTALS',
      title: 'State totals (K records) are in the file',
      detail: 'IRIS has no K record. Combined Federal/State filing is a setting on the submission, not a record.',
      fix: 'If you file through the Combined Federal/State program, set that up in IRIS rather than in the file.',
    });
  }

  const convertible =
    parsed.records.length > 0 && !findings.some((finding) => finding.severity === 'blocking');

  return {
    fileName,
    bytes: text.length,
    lines,
    taxYear: parsed.taxYear,
    transmitter,
    forms,
    issuers: parsed.issuers.map((issuer) => ({
      name: issuer.name,
      tin: issuer.tin,
      formType: issuer.formType,
      payees: issuer.recordCount,
    })),
    records: parsed.records,
    payees: parsed.records.length,
    names,
    amounts: [...amountsSeen.values()].sort((a, b) => a.code.localeCompare(b.code)),
    findings,
    foreignAddresses: parsed.foreignAddresses,
    sampleRecordLine,
    convertible,
  };
}

// ---------------------------------------------------------------------------
// Writing IRIS XML
// ---------------------------------------------------------------------------

/** Corrections a person made to a name split, by record reference. */
export type NameOverrides = Record<string, { first: string; last: string }>;

export interface XmlDocument {
  formType: string;
  /** 1 of 3, when a form's records need more than one transmission. */
  part: number;
  parts: number;
  payees: number;
  xml: string;
}

/**
 * The engine context the generators need, built from the file's own transmitter record. No
 * IRS connection, no credentials: the parts that matter for the XML are the TCC and the
 * transmitter's own details, and both are in the T record the customer already has.
 */
function browserContext(transmitter: Transmitter, taxYear: number | null) {
  const year = String(taxYear ?? new Date().getFullYear() - 1);
  return {
    irs: {
      name: transmitter.testFile ? 'ats' : 'live',
      tcc: transmitter.tcc || 'TCCXX',
      softwareId: '',
      maxIntakeTaxYear: Number(year),
    },
    transmitter: {
      paymentYear: year,
      transmitterTIN: transmitter.tin,
      transmitterControlCode: transmitter.tcc,
      testFileIndicator: transmitter.testFile ? 'T' : '',
      // Both name lines as the T record wrote them. One line is an issuer known by a single
      // name, which the generator writes as a BusinessName; two are a first and last name.
      transmitterName: transmitter.name,
      transmitterNameContinuation: transmitter.nameContinuation,
      companyName: transmitter.companyName,
      companyNameContinuation: transmitter.companyNameContinuation,
      // The schema wants the transmitter's address under CompanyGrp and the issuer's under
      // IssuerDetail. Here they are the same party - the customer's own T record - so the
      // one address answers for both, and leaving either out fails the IRS schema check.
      companyMailingAddress: transmitter.address,
      companyCity: transmitter.city,
      companyState: transmitter.state,
      companyZIPCode: transmitter.zip,
      IssuerTIN: transmitter.tin,
      IssuerShippingAddress: transmitter.address,
      IssuerCity: transmitter.city,
      IssuerState: transmitter.state,
      IssuerZIPCode: transmitter.zip,
      contactName: transmitter.contact,
      contactEmailAddress: transmitter.email,
      contactTelephoneNumber: transmitter.phone,
    },
    lookups: {
      // Combined Federal/State is an arrangement with the IRS, not something a file can say.
      cfsfEnabled: async () => false,
    },
    logger: undefined,
  } as unknown as EngineContextLike;
}

/**
 * The corrections a person made, plus the IRS record id every record carries into a
 * transmission. Tab Service mints that id when it writes the records; here it is minted the
 * same way, so the XML has a record id the IRS answers against.
 */
function prepare(records: CanonicalRecord[], overrides: NameOverrides): CanonicalRecord[] {
  return records.map((record) => {
    const fix = overrides[String(record.recordRef ?? '')];
    const keys = payeeKeys(formOf(record));
    const named: CanonicalRecord = fix
      ? { ...record, [keys.firstName]: fix.first, [keys.lastName]: fix.last }
      : { ...record };
    if (!named.id) named.id = generateRandomRecordId();
    return named;
  });
}

export async function toIrisXml(
  result: ConversionResult,
  overrides: NameOverrides = {},
): Promise<XmlDocument[]> {
  const ctx = browserContext(result.transmitter, result.taxYear);
  const records = prepare(result.records, overrides);
  const byForm = new Map<string, CanonicalRecord[]>();
  for (const record of records) {
    const formType = formOf(record, result.forms[0]?.formType);
    const list = byForm.get(formType);
    if (list) list.push(record);
    else byForm.set(formType, [record]);
  }

  const documents: XmlDocument[] = [];
  for (const [formType, formRecords] of byForm) {
    if (!SUPPORTED_FORMS.has(formType)) continue;
    const chunks = chunkRecords(formRecords);
    for (let index = 0; index < chunks.length; index += 1) {
      const generated = await dispatchXml(ctx, formType, chunks[index] as never[]);
      documents.push({
        formType,
        part: index + 1,
        parts: chunks.length,
        payees: chunks[index].length,
        // Most generators write every recipient as a USAddress; the filing service turns the
        // foreign ones into a ForeignAddress in its sanitizer pass, which does not run here.
        xml: rewriteForeignAddresses(generated.xml, chunks[index], irsCountryCode),
      });
    }
  }
  return documents;
}

// ---------------------------------------------------------------------------
// Writing the two reports
// ---------------------------------------------------------------------------

/**
 * True when a spreadsheet program would run the value as a formula: it starts with =, +, @,
 * a tab or a carriage return, or with a minus that is not a negative number. A payee name of
 * "=HYPERLINK(...)" in a client's file would otherwise run on the machine that opens the CSV.
 */
export function looksLikeFormula(value: string): boolean {
  return /^[=+@\t\r]/.test(value) || /^-(?![\d.])/.test(value);
}

/**
 * A cell for a CSV that people open in a spreadsheet (records, exceptions, reports): a
 * formula-like value is written with a leading apostrophe, so it shows as text and never runs.
 * Upload files are not written through this: there the value must stay as it is, and the
 * export stops instead (uploadLayout.ts).
 */
export function spreadsheetCell(value: unknown): string {
  const raw = value === null || value === undefined ? '' : String(value);
  const text = looksLikeFormula(raw) ? `'${raw}` : raw;
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

const csvCell = spreadsheetCell;

export function toCsv(result: ConversionResult, overrides: NameOverrides = {}): string {
  const records = prepare(result.records, overrides);
  const columns = [...new Set(records.flatMap((record) => Object.keys(record)))].filter(
    (key) => !key.startsWith('__'),
  );
  const rows = [columns.join(',')];
  for (const record of records) {
    rows.push(columns.map((column) => csvCell(record[column])).join(','));
  }
  return rows.join('\n');
}

export function toExceptionsCsv(result: ConversionResult): string {
  const rows = ['severity,code,line,payee,what,detail,what to do'];
  for (const finding of result.findings) {
    rows.push(
      [
        finding.severity,
        finding.code,
        finding.line ?? '',
        finding.payee ?? '',
        finding.title,
        finding.detail,
        finding.fix ?? '',
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return rows.join('\n');
}

/**
 * Every foreign address the file carried, as read: the original 51 characters beside the city,
 * province, postal code and country taken from them, and why a reading is not certain. This is
 * the list a person reviews, and the input for any later pass over the uncertain ones.
 */
export function toForeignAddressCsv(result: ConversionResult): string {
  const rows = [
    'line,whose,record,form,as written,city,province,province name,postal code,country code,country,confidence,why',
  ];
  for (const entry of result.foreignAddresses) {
    const r = entry.reading;
    rows.push(
      [
        entry.line,
        entry.role,
        entry.ref,
        entry.formType ?? '',
        r.raw,
        r.city,
        r.province,
        r.provinceName,
        r.postalCode,
        r.countryCode ?? '',
        r.countryName,
        r.confidence,
        r.reasons.join(' '),
      ]
        .map(csvCell)
        .join(','),
    );
  }
  return rows.join('\n');
}

/** Hands the browser a file. Nothing is uploaded; this is the same bytes going back out. */
export function download(name: string, text: string, type: string): void {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.rel = 'noopener';
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  // Taking the link out of the page in the same tick cancels the download in some browsers.
  window.setTimeout(() => {
    link.remove();
    URL.revokeObjectURL(url);
  }, 4000);
}
